/**
 * Life4Earth V2 — Public Landing Page
 *
 * Recreates the V1 landing page visual style and brand in React:
 *   1. Hero — video background + rotating quote carousel + inline OAuth login
 *   2. About — brief platform description
 *   3. Mission — side-by-side photo + text with section numbering
 *   4. Partners — carousel (placeholder until CMS populated)
 *   5. Rollout — four pillar-colored feature blocks + feature image
 *   6. Team — carousel (placeholder until CMS populated)
 *   7. Footer — address, contact, copyright, privacy
 *
 * Content is driven by:
 *   - i18n namespace "landing" for all user-facing strings
 *   - landingContent.ts data model for images, quotes, partners, team
 *
 * Does NOT use CSS custom property theming (standalone dark design).
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
// useRef retained for QuoteCarousel timerRef
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { DEFAULT_LANDING_CONTENT } from '../../Data/Landing/landingContent';
import type { LandingContent } from '../../Data/Landing/landingContent';
import { fetchLandingContent } from '../../api/admin';
import { mediaSrc } from '../../api/mediaUrl';
import LandingSignupForm from './LandingSignupForm';
import styles from './LandingPage.module.css';

// ── Constants ────────────────────────────────────────────────
const QUOTE_INTERVAL_MS = 7_000;

// ── L4E Wheel SVG (brand logo with spinning tricolor ring) ──
function L4EWheel({ size = 32 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="-15 -15 158 143"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
    >
      <g className={styles.wheelSpin}>
        <path
          d="M107.574 43.0177C114.543 45.6668 120.513 41.244 121.466 35.3008C143.023 81.9018 107.62 125.992 68.0824 127.581C60.1377 125.508 59.8821 115.902 67.9663 112.171C96.0285 110.051 123.301 78.3774 107.574 43.0177Z"
          fill="#45BB47"
        />
        <path
          d="M10.0098 29.7194C17.397 28.0148 22.0431 30.756 24.0409 37.7128C42.416 10.9915 82.1631 7.09848 103.744 36.4919C111.364 43.2643 121.213 35.3171 116.242 26.863C89.3877 -10.6159 35.1914 -8.15106 10.0098 29.7194Z"
          fill="#106DD5"
        />
        <path
          d="M60.3411 112.197C55.9274 116.413 55.1608 123.185 60.3411 128C11.8595 122.586 -10.9062 77.4365 5.05298 37.7692C10.303 29.8679 20.8264 34.0835 19.6881 44.1039C8.35168 67.3929 19.8972 107.728 60.3411 112.197Z"
          fill="#D53541"
        />
      </g>
      <path
        d="M64.1983 23.4648C41.7114 23.4648 23.4756 41.5478 23.4756 63.8463C23.4756 86.1447 41.7114 104.228 64.2216 104.228C86.7085 104.228 104.944 86.1447 104.944 63.8463C104.944 41.5478 86.7085 23.4648 64.1983 23.4648ZM63.9428 88.3561C50.4227 88.3561 39.4813 77.5064 39.4813 64.0997C39.4813 50.6929 50.4227 39.8432 63.9428 39.8432C77.4628 39.8432 88.4043 50.6929 88.4043 64.0997C88.4043 77.5064 77.4628 88.3561 63.9428 88.3561Z"
        fill="white"
      />
    </svg>
  );
}

// ── Quote Carousel ───────────────────────────────────────────

function QuoteCarousel({
  content,
  t,
}: {
  content: LandingContent;
  t: (key: string) => string;
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [fading, setFading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const quoteCount = content.quotes.length;

  const advance = useCallback(() => {
    setFading(true);
    setTimeout(() => {
      setActiveIndex((prev) => (prev + 1) % quoteCount);
      setFading(false);
    }, 400);
  }, [quoteCount]);

  useEffect(() => {
    timerRef.current = setInterval(advance, QUOTE_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [advance]);

  const quote = content.quotes[activeIndex];
  if (!quote) return null;

  return (
    <div className={styles.quoteCarousel}>
      <div className={`${styles.quoteItem} ${fading ? styles.quoteFading : ''}`}>
        <h1 className={styles.quoteText}>
          &ldquo;{quote.text || t(quote.textKey)}&rdquo;
        </h1>
        <p className={styles.quoteAuthor}>&mdash; {quote.author || t(quote.authorKey)}</p>
      </div>
      <div className={styles.quoteProgress}>
        {content.quotes.map((_, i) => (
          <button
            key={i}
            className={`${styles.quoteDot} ${i === activeIndex ? styles.quoteDotActive : ''}`}
            onClick={() => {
              if (timerRef.current) clearInterval(timerRef.current);
              setActiveIndex(i);
              timerRef.current = setInterval(advance, QUOTE_INTERVAL_MS);
            }}
            aria-label={`Quote ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

// ── Scroll-reveal hook ───────────────────────────────────────

function useScrollReveal() {
  const [visible, setVisible] = useState<Set<string>>(new Set());

  const ref = useCallback((node: HTMLElement | null) => {
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible((prev) => new Set(prev).add(entry.target.id));
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.12 }
    );
    observer.observe(node);
  }, []);

  return { visible, ref };
}

// ── Section Number Badge (V1 style) ─────────────────────────

function SectionTitle({
  name,
  number,
  lineDirection = 'left',
}: {
  name: string;
  number: number;
  lineDirection?: 'left' | 'right';
}) {
  return (
    <div className={styles.sectionTitle}>
      <div className={styles.sectionName}>{name}</div>
      <div
        className={`${styles.sectionLine} ${
          lineDirection === 'right' ? styles.sectionLineRight : ''
        }`}
      />
      <div className={styles.sectionNumber}>
        {String(number).padStart(2, '0')}
      </div>
    </div>
  );
}

// ── Scroll Position Widget (V1 style left-side nav) ─────────

function ScrollNav({
  sections,
  activeId,
}: {
  sections: { id: string; label: string }[];
  activeId: string;
}) {
  return (
    <nav className={styles.scrollNav}>
      {sections.map((s) => (
        <button
          key={s.id}
          className={`${styles.scrollNavItem} ${
            s.id === activeId ? styles.scrollNavItemActive : ''
          }`}
          onClick={() => {
            document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          <span>{s.label}</span>
        </button>
      ))}
    </nav>
  );
}

// ── Main LandingPage ─────────────────────────────────────────

export default function LandingPage() {
  const { t } = useAppTranslation('landing');
  const navigate = useNavigate();
  const { visible, ref: sectionRef } = useScrollReveal();
  const [content, setContent] = useState<LandingContent>(DEFAULT_LANDING_CONTENT);
  const [activeSection, setActiveSection] = useState('section-hero');

  // ── Fetch admin-managed content (fall back to defaults) ──
  useEffect(() => {
    (async () => {
      try {
        const saved = await fetchLandingContent();
        if (saved) setContent(saved);
      } catch {
        // API unavailable — use defaults
      }
    })();
  }, []);
  const [headerSmall, setHeaderSmall] = useState(false);

  // ── Override body overflow:hidden (set by index.css for the app shell) ──
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'auto';
    document.body.style.height = 'auto';
    const root = document.getElementById('root');
    const prevRootHeight = root?.style.height || '';
    if (root) root.style.height = 'auto';
    return () => {
      document.body.style.overflow = prev;
      document.body.style.height = '';
      if (root) root.style.height = prevRootHeight;
    };
  }, []);

  // ── Video source (responsive) ──
  const [videoSrc, setVideoSrc] = useState<string | null>(null);

  useEffect(() => {
    const w = window.innerWidth;
    if (w >= 768) {
      const suffix = w < 1200 ? '_540' : '';
      setVideoSrc(`${process.env.PUBLIC_URL}/content/video/${content.videoBaseName}${suffix}.mp4`);
    }
    // Mobile: no video, CSS bg image fallback
  }, [content.videoBaseName]);

  // ── Section definitions for scroll nav ──
  const sectionDefs = [
    { id: 'section-hero', label: t('scrollNav.hero') },
    { id: 'section-about', label: t('scrollNav.about') },
    { id: 'section-mission', label: t('scrollNav.mission') },
    ...(content.partners.length > 0
      ? [{ id: 'section-partners', label: t('scrollNav.partners') }]
      : []),
    { id: 'section-rollout', label: t('scrollNav.rollout') },
    ...(content.team.length > 0
      ? [{ id: 'section-team', label: t('scrollNav.team') }]
      : []),
  ];

  // ── Scroll listener for header shrink + active section ──
  useEffect(() => {
    const handleScroll = () => {
      const y = window.pageYOffset || document.documentElement.scrollTop;
      setHeaderSmall(y > 50);

      // Find active section
      for (let i = sectionDefs.length - 1; i >= 0; i--) {
        const el = document.getElementById(sectionDefs[i].id);
        if (el && el.offsetTop - 200 <= y) {
          setActiveSection(sectionDefs[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let partNum = 1;

  return (
    <div className={styles.landing}>
      {/* ─── FIXED HEADER ─── */}
      <header className={`${styles.header} ${headerSmall ? styles.headerSmall : ''}`}>
        <div className={styles.headerInner}>
          <a className={styles.logo} href="/" onClick={(e) => { e.preventDefault(); navigate('/welcome'); }}>
            <img
              src={`${process.env.PUBLIC_URL}/l4e-logo.svg`}
              alt="Life4Earth"
              className={styles.logoIcon}
            />
            <div className={styles.logoText}>
              <span className={styles.logoLife}>Life</span>
              <span>4Earth</span>
              <span className={styles.logoBeta}>{t('nav.betaLabel')}</span>
            </div>
          </a>
        </div>
      </header>

      {/* ─── SCROLL NAV (left side) ─── */}
      <ScrollNav sections={sectionDefs} activeId={activeSection} />

      {/* ─── HERO ─── */}
      <section id="section-hero" className={styles.hero}>
        {/* Layer 0: fallback bg image (shows on mobile / when video unavailable) */}
        <div
          className={styles.heroOverlay}
          style={{ backgroundImage: `url(${process.env.PUBLIC_URL}${content.heroBgImage})` }}
        />
        {/* Layer 1: video (desktop/tablet only) */}
        {videoSrc && (
          <div className={styles.heroVideo}>
            <video
              className={styles.heroVideoEl}
              autoPlay
              playsInline
              preload="auto"
              muted
              loop
              src={videoSrc}
            />
          </div>
        )}

        <div className={styles.heroContent}>
          <div className={styles.heroLeft}>
            <QuoteCarousel content={content} t={t} />
          </div>
          <div className={styles.heroRight}>
            <LandingSignupForm />
          </div>
        </div>
      </section>

      {/* ─── ABOUT ─── */}
      <section
        id="section-about"
        ref={sectionRef}
        className={`${styles.sectionDark} ${visible.has('section-about') ? styles.sectionVisible : ''}`}
      >
        <div className={styles.sectionContainer}>
          <h3 className={styles.aboutHeading}>{t('about.text1')}</h3>
          <div className={styles.aboutBody}>{t('about.text2')}</div>
        </div>
      </section>

      {/* ─── MISSION ─── */}
      <section
        id="section-mission"
        ref={sectionRef}
        className={`${styles.sectionDark} ${visible.has('section-mission') ? styles.sectionVisible : ''}`}
      >
        <div className={styles.sectionContainer}>
          <div className={styles.missionGrid}>
            <div className={styles.missionImageWrap}>
              <img
                src={`${process.env.PUBLIC_URL}${content.missionImage}`}
                alt={t('mission.title')}
                className={styles.missionImage}
              />
            </div>
            <div className={styles.missionTextWrap}>
              <SectionTitle
                name={t('mission.title')}
                number={partNum++}
                lineDirection="left"
              />
              <h3 className={styles.missionHeading}>{t('mission.heading')}</h3>
              <p className={styles.missionText}>{t('mission.text')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── PARTNERS (only if content exists) ─── */}
      {content.partners.length > 0 && (
        <section
          id="section-partners"
          ref={sectionRef}
          className={`${styles.sectionLight} ${visible.has('section-partners') ? styles.sectionVisible : ''}`}
        >
          <div className={styles.sectionContainer}>
            <SectionTitle
              name={t('partners.title')}
              number={partNum++}
              lineDirection="right"
            />
            <div className={styles.partnersGrid}>
              {content.partners.map((p, i) => (
                <div key={i} className={styles.partnerCard}>
                  <div className={styles.partnerLogoWrap}>
                    {p.logoUrl ? (
                      <img src={mediaSrc(p.logoUrl) ?? undefined} alt={p.name} className={styles.partnerLogo} />
                    ) : (
                      <div className={styles.partnerPlaceholder}>
                        {p.name.charAt(0)}
                      </div>
                    )}
                  </div>
                  <div className={styles.partnerName}>{p.name}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── ROLLOUT ─── */}
      <section
        id="section-rollout"
        ref={sectionRef}
        className={`${styles.sectionDark} ${visible.has('section-rollout') ? styles.sectionVisible : ''}`}
      >
        <div className={styles.sectionContainer}>
          <SectionTitle
            name={t('rollout.title')}
            number={partNum++}
            lineDirection="left"
          />

          <div className={styles.rolloutGrid}>
            {content.rolloutBlocks.slice(0, 2).map((block, i) => (
              <div
                key={i}
                className={styles.rolloutBlock}
                style={{ '--rollout-color': block.color } as React.CSSProperties}
              >
                <div className={styles.rolloutBlockInner}>
                  <h3 className={styles.rolloutBlockHeading}>{block.heading || t(block.headingKey)}</h3>
                  <p className={styles.rolloutBlockText}>{block.text || t(block.textKey)}</p>
                </div>
              </div>
            ))}
          </div>

          <div className={styles.rolloutImageWrap}>
            <img
              src={`${process.env.PUBLIC_URL}${content.rolloutImage}`}
              alt={t('rollout.title')}
              className={styles.rolloutImage}
            />
          </div>

          <div className={styles.rolloutGrid}>
            {content.rolloutBlocks.slice(2, 4).map((block, i) => (
              <div
                key={i}
                className={styles.rolloutBlock}
                style={{ '--rollout-color': block.color } as React.CSSProperties}
              >
                <div className={styles.rolloutBlockInner}>
                  <h3 className={styles.rolloutBlockHeading}>{block.heading || t(block.headingKey)}</h3>
                  <p className={styles.rolloutBlockText}>{block.text || t(block.textKey)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── TEAM (only if content exists) ─── */}
      {content.team.length > 0 && (
        <section
          id="section-team"
          ref={sectionRef}
          className={`${styles.sectionLight} ${visible.has('section-team') ? styles.sectionVisible : ''}`}
        >
          <div className={styles.sectionContainer}>
            <SectionTitle
              name={t('team.title')}
              number={partNum++}
              lineDirection="right"
            />
            <div className={styles.teamGrid}>
              {content.team.map((member, i) => (
                <div key={i} className={styles.teamCard}>
                  <div className={styles.teamImageWrap}>
                    {member.imageUrl ? (
                      <img
                        src={mediaSrc(member.imageUrl) ?? undefined}
                        alt={member.name}
                        className={styles.teamImage}
                      />
                    ) : (
                      <div className={styles.teamInitials}>
                        {member.name.split(/\s+/).map((w) => w.charAt(0)).slice(0, 2).join('')}
                      </div>
                    )}
                  </div>
                  <div className={styles.teamInfo}>
                    <h4 className={styles.teamName}>{member.name}</h4>
                    <div className={styles.teamPosition}>{member.position}</div>
                    {member.comment && (
                      <p className={styles.teamComment}>{member.comment}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── FOOTER ─── */}
      <footer className={styles.footer}>
        <div className={styles.sectionContainer}>
          <div className={styles.footerContent}>
            <div className={styles.footerAddress}>{t('footer.address')}</div>
            <div className={styles.footerContact}>
              {t('footer.contactUs')}:{' '}
              <a href={`mailto:${t('footer.email')}`} className={styles.footerLink}>
                {t('footer.email')}
              </a>
            </div>
            <div className={styles.footerCopyright}>
              {t('footer.copyright', { year: new Date().getFullYear() })}
            </div>
            <div className={styles.footerPrivacy}>{t('footer.privacy')}</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
